import React, { FC, useEffect, useState } from "react";
import { formatISO, getHours, startOfDay } from "date-fns";

import { useLabels } from "../../Hooks/useLabels";

import { Times } from "../../Helpers/interfaces";
import { DoctorDto } from "@apiModels/DoctorDto.csharp";
import { LoadingAnimation } from "../../../LoadingAnimation/LoadingAnimation";
import { HoursPickerTimes } from "./HoursPickerTimes";
import { HourPickerSection } from "./HourPickerSection";
import { AppointmentDto } from "../../../../../Api/Model/AppointmentDto.csharp";
import { LocationDto } from "../../../../../Api/Model/LocationDto.csharp";
import { ServiceContainerDto } from "../../../../../Api/Model/ServiceContainerDto.csharp";
import { RelatedServiceSetup } from "@apiModels/RelatedServiceSetup.csharp";
import { RelatedServicePriority } from "../../../../../Model/RelatedServicePriority.csharp";

type HourPickerProps = {
  availableAppointments: AppointmentDto[];
  handleTimeChange: (selection: AppointmentDto) => void;
  loading: boolean;
  calendarLoading: boolean;
  selectedAppointment?: AppointmentDto;
  selectedDay: Date;
  doctors: DoctorDto[];
  centers: LocationDto[];
  availableDays: Date[];
  goToNextAvailableDay: (date: Date) => void;
  selectedDoctor: DoctorDto;
  selectedLocation: LocationDto;
  selectedService: ServiceContainerDto;
  relatedServiceSetups: RelatedServiceSetup[];
  firstAvailableDate: Date | null;
};

export const HourPicker: FC<HourPickerProps> = ({
  availableAppointments,
  handleTimeChange,
  loading,
  calendarLoading,
  selectedAppointment,
  selectedDay,
  doctors,
  centers,
  availableDays,
  goToNextAvailableDay,
  selectedDoctor,
  selectedLocation,
  selectedService,
  relatedServiceSetups,
  firstAvailableDate,
}) => {
  const { bookingLabels } = useLabels();

  const [groupedAppointments, setGroupedAppointments] = useState<
    AppointmentDto[]
  >([]);

  const secondStepLabels = bookingLabels?.stepsBookingLabels.secondStepLabels;

  useEffect(() => {
    const getGroupedAppointments = (groups: AppointmentDto[][]) => {
      return groups.flatMap((group) => {
        const appointmentsFromGroup: AppointmentDto[] = [];

        const appointmentsMapByRelatedService = new Map<
          number,
          AppointmentDto[]
        >(Array.from(group, (a) => [a.relatedServiceId ?? -1, []]));

        group.forEach((a) =>
          appointmentsMapByRelatedService.get(a.relatedServiceId ?? -1)?.push(a)
        );

        const normalAppointments = appointmentsMapByRelatedService.get(-1);

        if (appointmentsMapByRelatedService.size === 1 && !!normalAppointments)
          return normalAppointments.takeRandom();

        for (const key of appointmentsMapByRelatedService.keys()) {
          const relatedService = relatedServiceSetups.find(
            (rs) => rs.relatedServiceId === key
          );
          if (!relatedService) continue;

          const relatedAppointments = appointmentsMapByRelatedService.get(key)!;

          switch (relatedService.priority) {
            case RelatedServicePriority.Both:
              appointmentsFromGroup.unshift(relatedAppointments.takeRandom());
              if (
                !!normalAppointments &&
                !appointmentsFromGroup.some((a) => !a.isRelatedAppointment)
              ) {
                appointmentsFromGroup.unshift(normalAppointments.takeRandom());
              }
              break;
            case RelatedServicePriority.Main:
              if (appointmentsFromGroup.some((a) => !a.isRelatedAppointment))
                continue;
              if (!normalAppointments) {
                appointmentsFromGroup.unshift(relatedAppointments.takeRandom());
                continue;
              }
              appointmentsFromGroup.unshift(normalAppointments.takeRandom());
              break;
            default:
              const concatenatedAppointments = relatedAppointments.concat(
                normalAppointments ?? []
              );
              const randomAppointment = concatenatedAppointments.takeRandom();
              if (
                appointmentsFromGroup.some((a) => !a.isRelatedAppointment) &&
                !randomAppointment.isRelatedAppointment
              )
                continue;
              appointmentsFromGroup.unshift(randomAppointment);
          }
        }
        return appointmentsFromGroup;
      });
    };

    const map = new Map<string, AppointmentDto[]>(
      Array.from(availableAppointments, (a) => [formatISO(a.startTime), []])
    );

    availableAppointments.forEach((a) =>
      map.get(formatISO(a.startTime))?.push(a)
    );

    const groups = Array.from(map.values());
    setGroupedAppointments(getGroupedAppointments(groups));
  }, [availableAppointments]);

  const [morningAppointments, afternoonAppointments, eveningAppointments] = [
    groupedAppointments.filter(
      (appointment) =>
        getHours(appointment.startTime) < HoursPickerTimes.morning
    ),
    groupedAppointments.filter(
      (appointment) =>
        getHours(appointment.startTime) >= HoursPickerTimes.morning &&
        getHours(appointment.startTime) < HoursPickerTimes.afternoon
    ),
    groupedAppointments.filter(
      (appointment) =>
        getHours(appointment.startTime) >= HoursPickerTimes.afternoon
    ),
  ];

  const futureDates = availableDays.filter(
    (date) => startOfDay(date) > selectedDay
  );
  const [firstMorning] = futureDates.filter(
    (date) => getHours(date) < HoursPickerTimes.morning
  );
  const [firstAfternoon] = futureDates.filter(
    (date) =>
      getHours(date) >= HoursPickerTimes.morning &&
      getHours(date) < HoursPickerTimes.afternoon
  );
  const [firstEvening] = futureDates.filter(
    (date) => getHours(date) >= HoursPickerTimes.afternoon
  );

  const times: Times = {
    morning: {
      appointments: morningAppointments,
      label: "Morgen",
      nextAvailableDate: firstMorning,
      nextAvailableDateLabel: secondStepLabels!.nextAvailableAppointmentMorning,
    },
    afternoon: {
      appointments: afternoonAppointments,
      label: "Dag",
      nextAvailableDate: firstAfternoon,
      nextAvailableDateLabel: secondStepLabels!.nextAvailableAppointmentDay,
    },
    evening: {
      appointments: eveningAppointments,
      label: "Kveld",
      nextAvailableDate: firstEvening,
      nextAvailableDateLabel: secondStepLabels!.nextAvailableAppointmentEvening,
    },
  };

  return (
    <div className="HourPicker">
      {loading || calendarLoading ? (
        <LoadingAnimation
          text="Laster"
          className="loading-animation-container text-center"
        />
      ) : (
        <>
          {availableAppointments.length != 0 ? (
            <>
              <HourPickerSection
                selectedAppointment={selectedAppointment}
                doctors={doctors}
                centers={centers}
                onSelect={handleTimeChange}
                goToNextAvailableDay={goToNextAvailableDay}
                selectedDoctor={selectedDoctor}
                selectedLocation={selectedLocation}
                selectedService={selectedService}
                relatedServiceSetups={relatedServiceSetups}
                {...times.morning}
              />
              <HourPickerSection
                selectedAppointment={selectedAppointment}
                doctors={doctors}
                centers={centers}
                onSelect={handleTimeChange}
                goToNextAvailableDay={goToNextAvailableDay}
                selectedDoctor={selectedDoctor}
                selectedLocation={selectedLocation}
                selectedService={selectedService}
                relatedServiceSetups={relatedServiceSetups}
                {...times.afternoon}
              />
              <HourPickerSection
                selectedAppointment={selectedAppointment}
                doctors={doctors}
                centers={centers}
                onSelect={handleTimeChange}
                selectedDoctor={selectedDoctor}
                selectedLocation={selectedLocation}
                goToNextAvailableDay={goToNextAvailableDay}
                selectedService={selectedService}
                relatedServiceSetups={relatedServiceSetups}
                {...times.evening}
              />
            </>
          ) : firstAvailableDate === null ? (
            <div
              dangerouslySetInnerHTML={{
                __html:
                  secondStepLabels?.noAppointmentsAllDay ?? "Ingen avtaler",
              }}
            />
          ) : null}
        </>
      )}
    </div>
  );
};
